<template>
  <div class="page-view">
    <div class="header-row">
      <div>
        <h2>登录日志</h2>
        <p>记录管理后台和小程序的登录时间、IP、设备、结果。</p>
      </div>
      <el-button type="primary" plain @click="loadData">刷新</el-button>
    </div>

    <div class="brand-card filter-card">
      <div class="toolbar-row">
        <el-input v-model.trim="filters.keyword" placeholder="账号 / 姓名 / IP / 设备 / 失败原因" clearable />
        <el-select v-model="filters.client_type" placeholder="端类型" clearable>
          <el-option label="管理后台" value="admin_web" />
          <el-option label="小程序" value="mini_program" />
        </el-select>
        <el-select v-model="filters.login_mode" placeholder="登录方式" clearable>
          <el-option label="账号密码" value="password" />
          <el-option label="短信验证码" value="sms" />
          <el-option label="二次短信验证" value="sms_verify" />
        </el-select>
        <el-select v-model="filters.result" placeholder="结果" clearable>
          <el-option label="成功" value="success" />
          <el-option label="失败" value="failed" />
        </el-select>
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          value-format="yyyy-MM-dd"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          unlink-panels
        />
        <el-button type="primary" plain @click="handleQuery">查询</el-button>
      </div>
    </div>

    <div class="brand-card table-card">
      <el-table :data="items" stripe>
        <el-table-column prop="created_at" label="时间" min-width="180" />
        <el-table-column prop="username" label="账号" min-width="120" />
        <el-table-column prop="name" label="姓名" min-width="120" />
        <el-table-column prop="role" label="角色" min-width="100">
          <template slot-scope="{ row }">{{ roleText(row.role) }}</template>
        </el-table-column>
        <el-table-column label="登录位置" min-width="180">
          <template slot-scope="{ row }">
            <el-button
              v-if="row.role === 'courier' && hasLoginLocation(row)"
              type="text"
              @click="openLoginLocation(row)"
            >
              {{ loginLocationText(row) }}
            </el-button>
            <span v-else>{{ row.role === "courier" ? "未获取" : "-" }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="client_type" label="端类型" min-width="110">
          <template slot-scope="{ row }">{{ clientTypeLabel(row.client_type) }}</template>
        </el-table-column>
        <el-table-column prop="login_mode" label="登录方式" min-width="120">
          <template slot-scope="{ row }">{{ loginModeLabel(row.login_mode) }}</template>
        </el-table-column>
        <el-table-column prop="ip" label="IP" min-width="140" />
        <el-table-column prop="device" label="设备" min-width="220" show-overflow-tooltip />
        <el-table-column prop="result" label="结果" min-width="90">
          <template slot-scope="{ row }">
            <span :style="{ color: row.result === 'success' ? '#19be6b' : '#f56c6c' }">
              {{ row.result === "success" ? "成功" : "失败" }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="failure_reason" label="失败原因" min-width="180" show-overflow-tooltip />
      </el-table>
      <div class="pager-row">
        <el-pagination
          background
          layout="total, prev, pager, next, sizes"
          :total="total"
          :page-size="pager.page_size"
          :current-page="pager.page"
          :page-sizes="[10, 20, 50]"
          @current-change="onPageChange"
          @size-change="onSizeChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { fetchLocationSettings, fetchLoginLogs } from "../api/orders";
import { roleLabel } from "../utils/auth";

export default {
  data() {
    return {
      filters: {
        keyword: "",
        client_type: "",
        login_mode: "",
        result: "",
      },
      dateRange: [],
      items: [],
      total: 0,
      pager: { page: 1, page_size: 10 },
      locationSettings: {},
    };
  },
  mounted() {
    this.loadLocationSettings();
    this.loadData();
  },
  methods: {
    async loadLocationSettings() {
      try {
        const { data } = await fetchLocationSettings();
        this.locationSettings = data || {};
      } catch (error) {
        this.locationSettings = {};
      }
    },
    async loadData() {
      const [created_start, created_end] = this.dateRange || [];
      const { data } = await fetchLoginLogs({
        keyword: this.filters.keyword || undefined,
        client_type: this.filters.client_type || undefined,
        login_mode: this.filters.login_mode || undefined,
        result: this.filters.result || undefined,
        created_start: created_start || undefined,
        created_end: created_end || undefined,
        page: this.pager.page,
        page_size: this.pager.page_size,
      });
      this.items = data.items || [];
      this.total = data.total || 0;
    },
    handleQuery() {
      this.pager.page = 1;
      this.loadData();
    },
    onPageChange(page) {
      this.pager.page = page;
      this.loadData();
    },
    onSizeChange(size) {
      this.pager.page = 1;
      this.pager.page_size = size;
      this.loadData();
    },
    roleText(role) {
      return roleLabel(role);
    },
    clientTypeLabel(value) {
      return value === "mini_program" ? "小程序" : "管理后台";
    },
    loginModeLabel(value) {
      if (value === "sms") return "短信验证码";
      if (value === "sms_verify") return "二次短信验证";
      return "账号密码";
    },
    hasLoginLocation(row) {
      if (
        row.login_latitude === null ||
        row.login_latitude === undefined ||
        row.login_latitude === "" ||
        row.login_longitude === null ||
        row.login_longitude === undefined ||
        row.login_longitude === ""
      ) {
        return false;
      }
      const lat = Number(row.login_latitude);
      const lng = Number(row.login_longitude);
      return Number.isFinite(lat) && Number.isFinite(lng);
    },
    loginLocationText(row) {
      const lat = Number(row.login_latitude);
      const lng = Number(row.login_longitude);
      return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    },
    openLoginLocation(row) {
      const lat = Number(row.login_latitude);
      const lng = Number(row.login_longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      const title = encodeURIComponent(`${row.name || row.username || "配送员"}登录位置`);
      const key = (this.locationSettings && this.locationSettings.tencent_map_key) || "";
      const referer = encodeURIComponent("配送系统");
      const url = `https://apis.map.qq.com/tools/poimarker?type=0&marker=coord:${lat},${lng};title:${title}&key=${encodeURIComponent(key)}&referer=${referer}`;
      window.open(url, "_blank");
    },
  },
};
</script>

<style scoped>
</style>
